import useAuth from "../useAuth";
import { usePermissions } from "../../utils/permissions";
import { Cap, Level, RbacLevel } from "../../utils/rbacCatalog";
import { useGetAgendaConfig } from ".";

type Permissions = ReturnType<typeof usePermissions>;

/** The agenda's rights, as its routes check them on the server. */
export const agendaRights = (permissions: Permissions) => {
  const hasFeature = permissions.hasFeatureAccess("AGENDA");
  const agenda = (level: RbacLevel) =>
    hasFeature && permissions.can(Cap.USERS_AGENDA, level);
  const canView = agenda(Level.VIEW);
  return {
    hasFeature,
    canView,
    canWrite: agenda(Level.WRITE),
    canDelete: agenda(Level.DELETE),
    /** Manages another person's agenda like one's own, and sees every agenda. */
    managesOthers: canView && permissions.can(Cap.USERS_DETAILS, Level.VIEW),
    /** The configuration and the types are read with `canView`, which the page needs anyway. */
    canConfigure: canView && permissions.can(Cap.AGENDA_SETTINGS, Level.WRITE),
  };
};

export const useAgendaAccess = (customerId: string, entityId: string) => {
  const { user } = useAuth();
  const permissions = usePermissions(user);
  const rights = agendaRights(permissions);
  const config = useGetAgendaConfig(customerId, entityId, {
    enabled: rights.canView,
  });
  const isOn = Boolean(config.data?.enabled);

  return {
    ...rights,
    config,
    isOn,
    canCreate: rights.canWrite && isOn,
  };
};

export type AgendaAccess = ReturnType<typeof useAgendaAccess>;
